import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useChildFeedPages } from '../../hooks/useChildFeedPages';
import {
  useGetMyObservationsUnreadCountQuery,
  useListMyChildIncidentsQuery,
  useListMyObservationsQuery,
  useMarkAllObservationsReadMutation,
} from '../../store/api/localApi';
import { MessageDeliveryStatus } from '../../types';
import FeedItemCard from '../../components/parent/FeedItemCard';
import IncidentSeverityChip from '../../components/incidents/IncidentSeverityChip';
import { formatDublinDate } from '../../utils/date';
import { mergeUpdates } from './updates';

type Filter = 'all' | 'observations' | 'moments' | 'incidents';
const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'observations', label: 'Observations' },
  { key: 'moments', label: 'Moments' },
  { key: 'incidents', label: 'Incidents' },
];

/** /parent/:studentId/updates — everything the service has told this parent about one child. */
const ParentChildUpdates: React.FC = () => {
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();
  if (isLoading) return <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">Loading…</div>;
  if (!activeStudent) return <Navigate to={parentHomePath ?? '/parent'} replace />;
  // Keyed: a different child is a different list, so no state (filter, feed
  // pages, cursor) carries across a switch.
  return <UpdatesList key={activeStudent.id} studentId={activeStudent.id} />;
};

const UpdatesList: React.FC<{ studentId: string }> = ({ studentId }) => {
  const [filter, setFilter] = useState<Filter>('all');
  const feed = useChildFeedPages(studentId);
  const { data: incidentsData } = useListMyChildIncidentsQuery({ childId: studentId });
  const { data: observationsData } = useListMyObservationsQuery({ limit: 50 });
  const [markAllObservationsRead, { isLoading: isMarkingAll }] = useMarkAllObservationsReadMutation();
  // The button follows the exact server count (the same one as the Updates
  // badge); the 50-row delivery list below only drives the per-item dots.
  const { data: unreadCountData } = useGetMyObservationsUnreadCountQuery({ studentId });
  const nothingUnread = (unreadCountData?.count ?? 0) === 0;

  const unreadObservationIds = useMemo(
    () =>
      new Set(
        (observationsData?.deliveries ?? [])
          .filter((d) => d.studentId === studentId && d.status === MessageDeliveryStatus.UNREAD)
          .map((d) => d.observationId)
      ),
    [observationsData, studentId]
  );
  // Scoped to this child: one child's page must not clear a sibling's unread state.
  const markAllRead = () => {
    markAllObservationsRead({ studentId });
  };

  const merged = useMemo(
    () => mergeUpdates(feed.items, incidentsData?.incidents ?? [], !feed.nextCursor && !feed.isLoading),
    [feed.items, feed.nextCursor, feed.isLoading, incidentsData]
  );
  const visible = merged.filter((item) =>
    filter === 'all' ? true
    : filter === 'incidents' ? item.kind === 'INCIDENT'
    : filter === 'moments' ? item.kind === 'MOMENT'
    : item.kind === 'OBSERVATION'
  );

  // Infinite scroll: load the next page when the sentinel below the list scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { nextCursor, isFetching, loadMore } = feed;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !nextCursor || isFetching) return;
    const observer = new IntersectionObserver((entries) => { if (entries[0]?.isIntersecting) loadMore(); }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, isFetching, loadMore]);

  const base = `/parent/${studentId}`;
  return (
    <div className="py-6" data-testid="parent-updates">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Updates</h1>
          <button type="button" onClick={() => markAllRead()} disabled={nothingUnread || isMarkingAll} data-testid="updates-mark-all-read" className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {isMarkingAll ? 'Marking…' : 'Mark all read'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="updates-filters">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`updates-filter-${f.key}`} aria-pressed={filter === f.key}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === f.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
        {/* While more pages remain, an empty filtered list is not "nothing here":
            the matching items (or the incidents held back until the feed ends)
            may be on a page not loaded yet. */}
        {(feed.isLoading || feed.nextCursor) && visible.length === 0 ? (
          <div data-testid="updates-loading" className="bg-white rounded-lg shadow p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div data-testid="updates-empty" className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">Nothing here yet</div>
        ) : (
          <ul className="space-y-2">
            {visible.map((item) =>
              item.kind === 'INCIDENT' ? (
                <li key={`incident-${item.id}`}>
                  <Link to={`${base}/incidents/${item.id}`} data-testid={`updates-incident-${item.id}`} className="bg-white rounded-lg shadow p-4 flex items-center gap-3 hover:shadow-md">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{item.incident.description}</div>
                      <div className="text-xs text-gray-500">Incident report · {formatDublinDate(item.incident.occurredAt)}</div>
                    </div>
                    <IncidentSeverityChip severity={item.incident.severity} />
                    <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                  </Link>
                </li>
              ) : (
                <li key={`${item.kind}-${item.id}`} className="relative">
                  {item.kind === 'OBSERVATION' && unreadObservationIds.has(item.id) && (
                    <span data-testid={`updates-item-unread-${item.id}`} aria-label="Unread" className="absolute left-2 top-2 h-2 w-2 rounded-full bg-blue-500" />
                  )}
                  <FeedItemCard item={item.feed} showDate href={item.kind === 'MOMENT' ? `${base}/moments/${item.id}` : `${base}/observations/${item.id}`} />
                </li>
              )
            )}
          </ul>
        )}
        {feed.nextCursor && (
          <div ref={sentinelRef} data-testid="parent-updates-more" className="h-8 flex items-center justify-center text-xs text-gray-400">
            {feed.isFetching ? 'Loading more…' : ''}
          </div>
        )}
      </div>
    </div>
  );
};
export default ParentChildUpdates;
